import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './RegistrationProgressBar.css';

const RegistrationProgressBar = ({ currentStep: propCurrentStep }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;
  
  // Get sex and assistance info from location state or localStorage
  const storedDraft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
  const sex = location.state?.sex || 'female';
  let isMinor = location.state?.isMinor || false;
  if (!isMinor) {
    const reasonForAssistance = storedDraft.reasonForAssistance || [];
    isMinor = Array.isArray(reasonForAssistance) 
      ? reasonForAssistance.includes('minor')
      : reasonForAssistance === 'minor';
  }
  const assistanceRequired = location.state?.assistanceRequired === 'yes' || storedDraft.assistanceRequired === 'yes';
  
  const reasonForAssistance = storedDraft.reasonForAssistance || [];
  const needsAccompanist = Array.isArray(reasonForAssistance)
      ? reasonForAssistance.some(r => ['pwd', 'senior', 'nrnw', 'others'].includes(r))
      : ['pwd', 'senior', 'nrnw', 'others'].includes(reasonForAssistance);
  
  // Steps configuration based on user type
  const minorFemaleSteps = [
    { id: 1, label: "Sign Up Checkpoint", route: '/signup-checkpoint' },
    { id: 2, label: "Personal Information", route: '/personal-info' },
    { id: 3, label: "Contact Details", route: '/contact-details' },
    { id: 4, label: "Guardian Contact Details", route: '/guardian-contact-details' },
    { id: 5, label: "Account Setup", route: '/account-setup' }
  ];

  const minorSteps = [
    { id: 1, label: "Sign Up Checkpoint", route: '/signup-checkpoint' },
    { id: 2, label: "Personal Information", route: '/personal-info' },
    { id: 3, label: "Contact Details", route: '/contact-details' },
    { id: 4, label: "Guardian Contact Details", route: '/guardian-contact-details' },
    { id: 5, label: "Account Setup", route: '/account-setup' }
  ];

  const accompanistFemaleSteps = [
    { id: 1, label: "Sign Up Checkpoint", route: '/signup-checkpoint' },
    { id: 2, label: "Personal Information", route: '/personal-info' },
    { id: 3, label: "Contact Details", route: '/contact-details' },
    { id: 4, label: "Accompanist Contact Details", route: '/accompanist-contact-details' },
    { id: 5, label: "Account Setup", route: '/account-setup' }
  ];

  const accompanistSteps = [
    { id: 1, label: "Sign Up Checkpoint", route: '/signup-checkpoint' },
    { id: 2, label: "Personal Information", route: '/personal-info' },
    { id: 3, label: "Contact Details", route: '/contact-details' },
    { id: 4, label: "Accompanist Contact Details", route: '/accompanist-contact-details' },
    { id: 5, label: "Account Setup", route: '/account-setup' }
  ];

  const femaleSteps = [
    { id: 1, label: "Sign Up Checkpoint", route: '/signup-checkpoint' },
    { id: 2, label: "Personal Information", route: '/personal-info' },
    { id: 3, label: "Contact Details", route: '/contact-details' },
    { id: 4, label: "Account Setup", route: '/account-setup' }
  ];

  const maleSteps = [
    { id: 1, label: "Sign Up Checkpoint", route: '/signup-checkpoint' },
    { id: 2, label: "Personal Information", route: '/personal-info' },
    { id: 3, label: "Contact Details", route: '/contact-details' },
    { id: 4, label: "Account Setup", route: '/account-setup' }
  ];

  // Determine which steps array to use based on user type AND current path
  let steps;
  if (currentPath === '/signup-checkpoint') {
    steps = sex === 'male' ? maleSteps : femaleSteps;
  } else if (currentPath === '/accompanist-contact-details') {
    steps = sex === 'female' ? accompanistFemaleSteps : accompanistSteps;
  } else if (currentPath === '/guardian-contact-details') {
    steps = sex === 'female' ? minorFemaleSteps : minorSteps;
  } else if (needsAccompanist) {
    steps = sex === 'female' ? accompanistFemaleSteps : accompanistSteps;
  } else if (isMinor) {
    steps = sex === 'female' ? minorFemaleSteps : minorSteps;
  } else {
    steps = sex === 'male' ? maleSteps : femaleSteps;
  }
  
  // Use prop currentStep or default to 1
  const currentStep = propCurrentStep || 1;
  
  // Calculate progress percentage
  const progressPercentage = (currentStep / steps.length) * 100;

  // Navigation handler
  const handleStepClick = (step) => {
    // Only allow navigation to completed steps or current step (not future steps)
    if (step.id > currentStep) return;
    
    // Get current registration data from localStorage
    const currentData = location.state || JSON.parse(localStorage.getItem('registrationDraft') || '{}');
    
    // Navigate to selected step
    if (step.route === '/guardian-contact-details') {
      navigate(step.route, { state: { ...currentData, isMinor: true } });
    } else {
      navigate(step.route, { state: currentData });
    }
  };

  
  return (
    <div className="progress-bar-container">
      
      {/* Progress Bar */}
      <div className="progress-bar-wrapper">
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        
        {/* Step Indicators */}
        <div className="step-indicators">
          {steps.map((step, index) => {
            const isActive = step.id === currentStep;
            const isCompleted = step.id < currentStep;
            const isClickable = isCompleted || isActive;
            
            return (
              <div
                key={step.id}
                className={`step-indicator ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
                onClick={() => isClickable && handleStepClick(step)}
                style={{ 
                  left: `${(index / (steps.length - 1)) * 100}%`,
                  cursor: isClickable ? 'pointer' : 'not-allowed'
                }}
                title={step.label}
              >
                <div className="step-dot">
                  {isCompleted ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                  ) : (
                    step.id
                  )}
                </div>
                <div className="step-tooltip">
                  {step.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Labels */}
      <div className="step-labels">
        {steps.map((step, index) => {
          const isActive = step.id === currentStep;
          const isCompleted = step.id < currentStep;
          const isClickable = isCompleted || isActive;
          
          return (
            <div
              key={step.id}
              className={`step-label ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              onClick={() => isClickable && handleStepClick(step)}
              style={{ 
                cursor: isClickable ? 'pointer' : 'not-allowed'
              }}
            >
              {step.label}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RegistrationProgressBar;
